import { createTheme, Theme, alpha } from '@mui/material/styles';
import { ptBR } from '@mui/material/locale';

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      sidebar: {
        width: number;
        collapsedWidth: number;
      };
      header: {
        height: number;
      };
    };
  }

  interface ThemeOptions {
    custom?: {
      sidebar?: {
        width?: number;
        collapsedWidth?: number;
      };
      header?: {
        height?: number;
      };
    };
  }

  interface PaletteColor {
    lighter?: string;
    darker?: string;
  }

  interface SimplePaletteColorOptions {
    lighter?: string;
    darker?: string;
  }
}

// Mantis-inspired color palette
const mantisColors = {
  primary: {
    lighter: '#E3F2FD',
    light: '#64B5F6',
    main: '#1976D2',
    dark: '#1565C0',
    darker: '#0D47A1',
  },
  secondary: {
    lighter: '#EDE7F6',
    light: '#B39DDB',
    main: '#673AB7',
    dark: '#5E35B1',
    darker: '#4527A0',
  },
  success: {
    lighter: '#E8F5E9',
    light: '#81C784',
    main: '#4CAF50',
    dark: '#388E3C',
    darker: '#1B5E20',
  },
  warning: {
    lighter: '#FFF8E1',
    light: '#FFD54F',
    main: '#FFC107',
    dark: '#FFA000',
    darker: '#FF6F00',
  },
  error: {
    lighter: '#FFEBEE',
    light: '#E57373',
    main: '#F44336',
    dark: '#D32F2F',
    darker: '#B71C1C',
  },
  info: {
    lighter: '#E1F5FE',
    light: '#4FC3F7',
    main: '#03A9F4',
    dark: '#0288D1',
    darker: '#01579B',
  },
};

export const createAppTheme = (
  mode: 'light' | 'dark' = 'light',
  primaryColor: string = mantisColors.primary.main,
  secondaryColor: string = mantisColors.secondary.main
): Theme => {
  const isLight = mode === 'light';

  const theme = createTheme(
    {
      palette: {
        mode,
        primary: {
          lighter: mantisColors.primary.lighter,
          light: mantisColors.primary.light,
          main: primaryColor,
          dark: mantisColors.primary.dark,
          darker: mantisColors.primary.darker,
          contrastText: '#fff',
        },
        secondary: {
          lighter: mantisColors.secondary.lighter,
          light: mantisColors.secondary.light,
          main: secondaryColor,
          dark: mantisColors.secondary.dark,
          darker: mantisColors.secondary.darker,
          contrastText: '#fff',
        },
        success: {
          lighter: mantisColors.success.lighter,
          light: mantisColors.success.light,
          main: mantisColors.success.main,
          dark: mantisColors.success.dark,
          darker: mantisColors.success.darker,
          contrastText: '#fff',
        },
        warning: {
          lighter: mantisColors.warning.lighter,
          light: mantisColors.warning.light,
          main: mantisColors.warning.main,
          dark: mantisColors.warning.dark,
          darker: mantisColors.warning.darker,
          contrastText: '#212121',
        },
        error: {
          lighter: mantisColors.error.lighter,
          light: mantisColors.error.light,
          main: mantisColors.error.main,
          dark: mantisColors.error.dark,
          darker: mantisColors.error.darker,
          contrastText: '#fff',
        },
        info: {
          lighter: mantisColors.info.lighter,
          light: mantisColors.info.light,
          main: mantisColors.info.main,
          dark: mantisColors.info.dark,
          darker: mantisColors.info.darker,
          contrastText: '#fff',
        },
        grey: {
          50: '#FAFAFA',
          100: '#F5F5F5',
          200: '#EEEEEE',
          300: '#E0E0E0',
          400: '#BDBDBD',
          500: '#9E9E9E',
          600: '#757575',
          700: '#616161',
          800: '#424242',
          900: '#212121',
        },
        background: {
          default: isLight ? '#FAFAFB' : '#121212',
          paper: isLight ? '#FFFFFF' : '#1E1E1E',
        },
        text: {
          primary: isLight ? '#262626' : '#FFFFFF',
          secondary: isLight ? '#8C8C8C' : '#A6A6A6',
          disabled: isLight ? '#BFBFBF' : '#595959',
        },
        divider: isLight ? '#F0F0F0' : '#303030',
        action: {
          hover: isLight ? alpha('#000', 0.04) : alpha('#fff', 0.08),
          selected: isLight ? alpha('#000', 0.08) : alpha('#fff', 0.12),
          disabled: isLight ? alpha('#000', 0.26) : alpha('#fff', 0.3),
          disabledBackground: isLight ? alpha('#000', 0.12) : alpha('#fff', 0.12),
        },
      },
      typography: {
        fontFamily: '"Inter", "Public Sans", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
          fontWeight: 700,
          fontSize: '2.375rem',
          lineHeight: 1.21,
        },
        h2: {
          fontWeight: 700,
          fontSize: '1.875rem',
          lineHeight: 1.27,
        },
        h3: {
          fontWeight: 600,
          fontSize: '1.5rem',
          lineHeight: 1.33,
        },
        h4: {
          fontWeight: 600,
          fontSize: '1.25rem',
          lineHeight: 1.4,
        },
        h5: {
          fontWeight: 600,
          fontSize: '1rem',
          lineHeight: 1.5,
        },
        h6: {
          fontWeight: 500,
          fontSize: '0.875rem',
          lineHeight: 1.57,
        },
        subtitle1: {
          fontSize: '0.875rem',
          fontWeight: 600,
          lineHeight: 1.57,
        },
        subtitle2: {
          fontSize: '0.75rem',
          fontWeight: 500,
          lineHeight: 1.66,
        },
        body1: {
          fontSize: '0.875rem',
          lineHeight: 1.57,
        },
        body2: {
          fontSize: '0.75rem',
          lineHeight: 1.66,
        },
        caption: {
          fontSize: '0.75rem',
          lineHeight: 1.66,
          color: isLight ? '#8C8C8C' : '#A6A6A6',
        },
        button: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
      shape: {
        borderRadius: 8,
      },
      shadows: [
        'none',
        '0px 1px 2px rgba(0, 0, 0, 0.03), 0px 1px 6px -1px rgba(0, 0, 0, 0.02), 0px 2px 4px rgba(0, 0, 0, 0.02)',
        '0px 1px 2px rgba(0, 0, 0, 0.04), 0px 1px 6px -1px rgba(0, 0, 0, 0.03), 0px 2px 4px rgba(0, 0, 0, 0.03)',
        '0px 1px 2px rgba(0, 0, 0, 0.05), 0px 2px 8px -2px rgba(0, 0, 0, 0.04), 0px 4px 6px rgba(0, 0, 0, 0.04)',
        '0px 2px 4px rgba(0, 0, 0, 0.06), 0px 4px 12px -4px rgba(0, 0, 0, 0.05), 0px 6px 10px rgba(0, 0, 0, 0.05)',
        '0px 3px 6px rgba(0, 0, 0, 0.07), 0px 6px 16px -6px rgba(0, 0, 0, 0.06), 0px 8px 14px rgba(0, 0, 0, 0.06)',
        '0px 4px 8px rgba(0, 0, 0, 0.08), 0px 8px 20px -8px rgba(0, 0, 0, 0.07), 0px 10px 18px rgba(0, 0, 0, 0.07)',
        '0px 5px 10px rgba(0, 0, 0, 0.09), 0px 10px 24px -10px rgba(0, 0, 0, 0.08), 0px 12px 22px rgba(0, 0, 0, 0.08)',
        '0px 6px 12px rgba(0, 0, 0, 0.1), 0px 12px 28px -12px rgba(0, 0, 0, 0.09), 0px 14px 26px rgba(0, 0, 0, 0.09)',
        '0px 7px 14px rgba(0, 0, 0, 0.11), 0px 14px 32px -14px rgba(0, 0, 0, 0.1), 0px 16px 30px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.12), 0px 16px 36px -16px rgba(0, 0, 0, 0.11), 0px 18px 34px rgba(0, 0, 0, 0.11)',
        '0px 9px 18px rgba(0, 0, 0, 0.13), 0px 18px 40px -18px rgba(0, 0, 0, 0.12), 0px 20px 38px rgba(0, 0, 0, 0.12)',
        '0px 10px 20px rgba(0, 0, 0, 0.14), 0px 20px 44px -20px rgba(0, 0, 0, 0.13), 0px 22px 42px rgba(0, 0, 0, 0.13)',
        '0px 11px 22px rgba(0, 0, 0, 0.15), 0px 22px 48px -22px rgba(0, 0, 0, 0.14), 0px 24px 46px rgba(0, 0, 0, 0.14)',
        '0px 12px 24px rgba(0, 0, 0, 0.16), 0px 24px 52px -24px rgba(0, 0, 0, 0.15), 0px 26px 50px rgba(0, 0, 0, 0.15)',
        '0px 13px 26px rgba(0, 0, 0, 0.17), 0px 26px 56px -26px rgba(0, 0, 0, 0.16), 0px 28px 54px rgba(0, 0, 0, 0.16)',
        '0px 14px 28px rgba(0, 0, 0, 0.18), 0px 28px 60px -28px rgba(0, 0, 0, 0.17), 0px 30px 58px rgba(0, 0, 0, 0.17)',
        '0px 15px 30px rgba(0, 0, 0, 0.19), 0px 30px 64px -30px rgba(0, 0, 0, 0.18), 0px 32px 62px rgba(0, 0, 0, 0.18)',
        '0px 16px 32px rgba(0, 0, 0, 0.2), 0px 32px 68px -32px rgba(0, 0, 0, 0.19), 0px 34px 66px rgba(0, 0, 0, 0.19)',
        '0px 17px 34px rgba(0, 0, 0, 0.21), 0px 34px 72px -34px rgba(0, 0, 0, 0.2), 0px 36px 70px rgba(0, 0, 0, 0.2)',
        '0px 18px 36px rgba(0, 0, 0, 0.22), 0px 36px 76px -36px rgba(0, 0, 0, 0.21), 0px 38px 74px rgba(0, 0, 0, 0.21)',
        '0px 19px 38px rgba(0, 0, 0, 0.23), 0px 38px 80px -38px rgba(0, 0, 0, 0.22), 0px 40px 78px rgba(0, 0, 0, 0.22)',
        '0px 20px 40px rgba(0, 0, 0, 0.24), 0px 40px 84px -40px rgba(0, 0, 0, 0.23), 0px 42px 82px rgba(0, 0, 0, 0.23)',
        '0px 21px 42px rgba(0, 0, 0, 0.25), 0px 42px 88px -42px rgba(0, 0, 0, 0.24), 0px 44px 86px rgba(0, 0, 0, 0.24)',
        '0px 22px 44px rgba(0, 0, 0, 0.26), 0px 44px 92px -44px rgba(0, 0, 0, 0.25), 0px 46px 90px rgba(0, 0, 0, 0.25)',
      ],
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            '*': {
              boxSizing: 'border-box',
            },
            html: {
              MozOsxFontSmoothing: 'grayscale',
              WebkitFontSmoothing: 'antialiased',
            },
            body: {
              backgroundColor: isLight ? '#FAFAFB' : '#121212',
            },
            '::-webkit-scrollbar': {
              width: 8,
              height: 8,
            },
            '::-webkit-scrollbar-track': {
              background: isLight ? '#F5F5F5' : '#2C2C2C',
            },
            '::-webkit-scrollbar-thumb': {
              background: isLight ? '#BDBDBD' : '#555',
              borderRadius: 4,
            },
            '::-webkit-scrollbar-thumb:hover': {
              background: isLight ? '#9E9E9E' : '#777',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              fontWeight: 500,
              borderRadius: 8,
              padding: '8px 20px',
              fontSize: '0.875rem',
            },
            contained: {
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px 0 rgba(0,0,0,0.15)',
              },
            },
            containedPrimary: {
              background: `linear-gradient(135deg, ${mantisColors.primary.main} 0%, ${mantisColors.primary.dark} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${mantisColors.primary.dark} 0%, ${mantisColors.primary.darker} 100%)`,
              },
            },
            outlined: {
              borderWidth: 1.5,
              '&:hover': {
                borderWidth: 1.5,
              },
            },
            sizeSmall: {
              padding: '6px 16px',
              fontSize: '0.75rem',
            },
            sizeLarge: {
              padding: '12px 28px',
              fontSize: '1rem',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              boxShadow: isLight
                ? '0px 2px 4px rgba(0, 0, 0, 0.02), 0px 4px 8px rgba(0, 0, 0, 0.04)'
                : '0px 2px 4px rgba(0, 0, 0, 0.2), 0px 4px 8px rgba(0, 0, 0, 0.2)',
              border: isLight ? '1px solid #F0F0F0' : '1px solid #303030',
              backgroundImage: 'none',
            },
          },
        },
        MuiCardHeader: {
          styleOverrides: {
            root: {
              padding: '20px 24px',
            },
            title: {
              fontSize: '1rem',
              fontWeight: 600,
            },
          },
        },
        MuiCardContent: {
          styleOverrides: {
            root: {
              padding: '24px',
              '&:last-child': {
                paddingBottom: 24,
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              backgroundImage: 'none',
            },
            elevation1: {
              boxShadow: isLight
                ? '0px 2px 4px rgba(0, 0, 0, 0.02), 0px 4px 8px rgba(0, 0, 0, 0.04)'
                : '0px 2px 4px rgba(0, 0, 0, 0.2), 0px 4px 8px rgba(0, 0, 0, 0.2)',
            },
          },
        },
        MuiTableHead: {
          styleOverrides: {
            root: {
              '& .MuiTableCell-root': {
                fontWeight: 600,
                backgroundColor: isLight ? '#FAFAFA' : '#262626',
                color: isLight ? '#262626' : '#FFFFFF',
                borderBottom: `1px solid ${isLight ? '#F0F0F0' : '#303030'}`,
              },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: `1px solid ${isLight ? '#F0F0F0' : '#303030'}`,
              padding: '16px',
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&:hover': {
                backgroundColor: isLight ? alpha('#1976D2', 0.04) : alpha('#fff', 0.04),
              },
              '&:last-child .MuiTableCell-root': {
                borderBottom: 'none',
              },
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                borderRadius: 8,
                '& fieldset': {
                  borderColor: isLight ? '#E0E0E0' : '#424242',
                },
                '&:hover fieldset': {
                  borderColor: mantisColors.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderWidth: 2,
                },
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              '& fieldset': {
                borderColor: isLight ? '#E0E0E0' : '#424242',
              },
              '&:hover fieldset': {
                borderColor: mantisColors.primary.main,
              },
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              borderBottom: `1px solid ${isLight ? '#F0F0F0' : '#303030'}`,
              backgroundColor: isLight ? '#FFFFFF' : '#1E1E1E',
              color: isLight ? '#262626' : '#FFFFFF',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              borderRight: `1px solid ${isLight ? '#F0F0F0' : '#303030'}`,
              boxShadow: 'none',
              backgroundColor: isLight ? '#FFFFFF' : '#1E1E1E',
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              margin: '4px 12px',
              padding: '10px 16px',
              '&:hover': {
                backgroundColor: isLight ? alpha(mantisColors.primary.main, 0.08) : alpha('#fff', 0.08),
              },
              '&.Mui-selected': {
                backgroundColor: isLight ? alpha(mantisColors.primary.main, 0.12) : alpha(mantisColors.primary.light, 0.16),
                color: mantisColors.primary.main,
                '& .MuiListItemIcon-root': {
                  color: mantisColors.primary.main,
                },
                '&:hover': {
                  backgroundColor: isLight ? alpha(mantisColors.primary.main, 0.16) : alpha(mantisColors.primary.light, 0.24),
                },
              },
            },
          },
        },
        MuiListItemIcon: {
          styleOverrides: {
            root: {
              minWidth: 36,
              color: isLight ? '#8C8C8C' : '#A6A6A6',
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 6,
              fontWeight: 500,
            },
            colorSuccess: {
              backgroundColor: alpha(mantisColors.success.main, 0.12),
              color: mantisColors.success.dark,
            },
            colorError: {
              backgroundColor: alpha(mantisColors.error.main, 0.12),
              color: mantisColors.error.dark,
            },
            colorWarning: {
              backgroundColor: alpha(mantisColors.warning.main, 0.12),
              color: mantisColors.warning.darker,
            },
            colorInfo: {
              backgroundColor: alpha(mantisColors.info.main, 0.12),
              color: mantisColors.info.dark,
            },
          },
        },
        MuiAlert: {
          styleOverrides: {
            root: {
              borderRadius: 8,
            },
            standardSuccess: {
              backgroundColor: alpha(mantisColors.success.main, 0.12),
              color: mantisColors.success.darker,
              '& .MuiAlert-icon': {
                color: mantisColors.success.main,
              },
            },
            standardError: {
              backgroundColor: alpha(mantisColors.error.main, 0.12),
              color: mantisColors.error.darker,
              '& .MuiAlert-icon': {
                color: mantisColors.error.main,
              },
            },
            standardWarning: {
              backgroundColor: alpha(mantisColors.warning.main, 0.12),
              color: mantisColors.warning.darker,
              '& .MuiAlert-icon': {
                color: mantisColors.warning.main,
              },
            },
            standardInfo: {
              backgroundColor: alpha(mantisColors.info.main, 0.12),
              color: mantisColors.info.darker,
              '& .MuiAlert-icon': {
                color: mantisColors.info.main,
              },
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              borderRadius: 6,
              fontSize: '0.75rem',
              backgroundColor: isLight ? '#262626' : '#FAFAFA',
              color: isLight ? '#FFFFFF' : '#262626',
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              borderRadius: 16,
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              fontSize: '1.125rem',
              fontWeight: 600,
              padding: '20px 24px',
            },
          },
        },
        MuiDialogContent: {
          styleOverrides: {
            root: {
              padding: '24px',
            },
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: {
              padding: '16px 24px',
              gap: 8,
            },
          },
        },
        MuiAvatar: {
          styleOverrides: {
            root: {
              fontWeight: 600,
            },
            colorDefault: {
              backgroundColor: isLight ? mantisColors.primary.lighter : mantisColors.primary.dark,
              color: isLight ? mantisColors.primary.dark : mantisColors.primary.lighter,
            },
          },
        },
        MuiBadge: {
          styleOverrides: {
            badge: {
              fontWeight: 600,
            },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: {
              borderRadius: 4,
              height: 6,
            },
            bar: {
              borderRadius: 4,
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            indicator: {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              fontWeight: 500,
              textTransform: 'none',
              minWidth: 'auto',
              padding: '12px 16px',
            },
          },
        },
      },
      custom: {
        sidebar: {
          width: 280,
          collapsedWidth: 72,
        },
        header: {
          height: 64,
        },
      },
    },
    ptBR
  );

  return theme;
};

// Export default theme
export const defaultTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');
